// Removed stream status config imports - using string literals
import { getCurrentProvider, switchProvider } from "@/lib/ai/providers/openai";
import { API_BASE_URL } from "@/lib/constants/app";
import { SupabaseThreadUtils } from "@/lib/db/utils/supabase/threads";
import { supabase } from "@/lib/supabase";
import supabaseAdmin from "@/lib/supabaseAdmin";
import {
  saveAssistantMessage,
  saveFollowUpMessage,
  saveToolCallMessage,
  saveUserMessage,
} from "@/lib/utils/ai/streaming/databaseOperations";
import { fileUtils } from "@/lib/utils/fileUtils.js";
import { fetchSharedThreadData } from "@/lib/utils/teamAccessUtils";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import useAssistantStore from "./assistantStore";
import { useAuthStore } from "./authStore";

export const INITIAL_STORE_STATE = {
  initialized: false,
  isLoading: false,
  error: null,
  initializing: false,
  abortController: null,
  preventNewMessageClear: false,
  pendingUserMessage: null,
  pendingUserMessageId: null,
  // Title generation state
  titleGenerationQueue: new Map(), // threadId -> { content, status, attempts, timestamp }
  titleGenerationInProgress: new Set(), // Set of threadIds currently generating titles
};
export const INITIAL_THREAD_CONTEXT_STATE = {
  annotations: [],
  attachments: [],
  citations: [],
  code_blocks: [],
  diagrams: [],
  documents: [],
  files: [],
  image_files: [],
  image_urls: [],
  images: [],
  message_count: 0,
  message_metadata: [],
  messages: [],
  metadata: {},
  search_results: [],
  stats: {
    annotations: 0,
    citations: 0,
    code: 0,
    code_blocks: 0,
    files: 0,
    image_files: 0,
    image_urls: 0,
    images: 0,
    messages: 0,
    summaries: 0,
    tool_calls: 0,
  },
  summaries: [],
  summary: "",
  tags: [],
  text_content: [],
  thread: null,
  thread_files: [],
  title: "",
  tool_output_ids: [],
  tool_resources: {},
  tool_calls: [],
  toolOutputs: [],
  toolState: {
    active: new Map(),
    errors: new Map(),
    inputs: new Map(),
    outputs: new Map(),
    pending: new Map(),
  },
  usage_history: [],
};
export const INITIAL_THREAD_STATE = {
  threads: [],
  threadMessages: new Map(),
  currentThread: null,
  currentThreadId: null,
  currentThreadTitle: null,
  currentThreadContext: INITIAL_THREAD_CONTEXT_STATE,
  currentThreadUsageHistory: [],
  currentUsage: {
    prompt_tokens: 0,
    completion_tokens: 0,
    prompt_token_details: {
      cached_tokens: 0,
    },
    total_tokens: 0,
  },
  threadMetadata: {},
  filteredThreads: [],
  // New chat thread management
  pendingNewChatThreadId: null,
  pendingNewChatCreatedAt: null,
  // Workspace message handling
  pendingWorkspaceMessage: null,
  // Deep research tracking
  activeDeepResearchThreadId: null,
  // Background streaming support
  activeStreams: new Map(), // Map<threadId, StreamMetadata>
  maxConcurrentStreams: 3, // Maximum number of concurrent streams
  // Optimistic message rendering
  optimisticMessages: new Map(), // Map<threadId, Message[]>
  // Real-time subscription channel
  threadSubscriptionChannel: null,
};
export const INITIAL_MESSAGE_STATE = {
  messages: [],
  messageAnnotations: new Map(),
  currentMessageId: null,
};
export const INITIAL_RUN_STATE = {
  activeRuns: new Map(),
  runSettings: {
    max_prompt_tokens: 0,
    max_completion_tokens: 0,
    truncation_strategy: "auto",
    tool_choice: "auto",
    parallel_tool_calls: true,
    response_format: "auto",
    temperature: 0.7,
    top_p: 1,
    additional_messages: null,
    instructions: null,
    additional_instructions: null,
    metadata: null,
    model: null,
  },
};
export const INITIAL_TOOL_STATE = {
  tool_calls: [],
  toolInputs: new Map(),
  toolOutputs: new Map(),
  toolState: {
    active: new Map(),
    pending: new Map(),
    inputs: new Map(),
    outputs: new Map(),
    errors: new Map(),
  },
  pendingToolOutputs: new Map(),
  currentToolCallId: null,
  currentToolCallName: null,
  currentToolCallOutput: null,
  streamingToolContent: "",
  streamingToolContentComplete: false,
};
export const INITIAL_STREAM_STATE = {
  streamingContent: "",
  isStreaming: false,
  streamStatus: "idle",
  messageStatus: "created",
};
export const INITIAL_COMPUTED_STATE = {
  searchResults: [],
};

export const useThreadStore = create(
  persist(
    (set, get) => ({
      ...INITIAL_STORE_STATE,
      ...INITIAL_MESSAGE_STATE,
      ...INITIAL_THREAD_STATE,
      ...INITIAL_RUN_STATE,
      ...INITIAL_TOOL_STATE,
      ...INITIAL_STREAM_STATE,
      ...INITIAL_COMPUTED_STATE,
      userId: null,
      currentWorkspaceId: null,
      currentAssistantId: null,
      setCurrentThread: (thread) => {
        // Load messages from threadMessages Map when switching threads
        const currentState = get();
        const threadMessagesMap = currentState.threadMessages || new Map();
        const threadMessages = thread?.id
          ? threadMessagesMap.get(thread.id) || []
          : [];

        // Set the thread and load its messages from the Map
        set({
          currentThread: thread,
          messages: threadMessages,
          currentThreadId: thread?.id || null,
        });
      },
      setCurrentThreadId: (threadId) => {
        // Load messages from threadMessages Map when switching threads
        const currentState = get();
        const threadMessagesMap = currentState.threadMessages || new Map();
        const storedMessages = threadId
          ? threadMessagesMap.get(threadId) || []
          : [];

        // VALIDATION: Filter messages to ensure they match the target thread
        // This prevents messages from other threads from appearing due to race conditions
        // NOTE: Messages without thread_id are allowed (legacy messages from before thread_id was enforced)
        const validMessages = storedMessages.filter((msg) => {
          if (!msg) return false;

          // If message has no thread_id, allow it (legacy message or pre-thread_id enforcement)
          if (!msg.thread_id) {
            return true;
          }

          // Only filter out messages that have a DIFFERENT thread_id
          if (msg.thread_id !== threadId) {
            console.warn(
              "[threadStore] Filtered out message from wrong thread:",
              {
                messageId: msg.id,
                messageThread: msg.thread_id,
                targetThread: threadId,
              }
            );
            return false;
          }

          return true;
        });

        if (validMessages.length !== storedMessages.length) {
          console.warn(
            `[threadStore] Filtered ${storedMessages.length - validMessages.length} messages with wrong thread_id when loading thread ${threadId}`
          );
        }

        // Set the thread ID and load only valid messages from the Map
        set({
          currentThreadId: threadId,
          messages: validMessages,
        });
      },
      setCurrentThreadTitle: (title) => set({ currentThreadTitle: title }),
      setCurrentThreadContext: (context) =>
        set({ currentThreadContext: context }),

      // Deep research tracking
      setActiveDeepResearchThreadId: (threadId) =>
        set({ activeDeepResearchThreadId: threadId }),

      getActiveDeepResearchThreadId: () => get().activeDeepResearchThreadId,

      // New chat thread management - centralized in thread store
      setPendingNewChatThread: (threadId) => {
        set((state) => ({
          pendingNewChatThreadId: threadId,
          pendingNewChatCreatedAt: Date.now(),
        }));
      },

      getPendingNewChatThread: () => {
        const state = get();
        const { pendingNewChatThreadId, pendingNewChatCreatedAt } = state;

        // Check if the pending thread is still valid (within 30 seconds)
        if (pendingNewChatThreadId && pendingNewChatCreatedAt) {
          const isValid = Date.now() - pendingNewChatCreatedAt < 30000;
          if (isValid) {
            return pendingNewChatThreadId;
          } else {
            // Clean up expired pending thread
            set({
              pendingNewChatThreadId: null,
              pendingNewChatCreatedAt: null,
            });
          }
        }

        return null;
      },

      clearPendingNewChatThread: () => {
        set({
          pendingNewChatThreadId: null,
          pendingNewChatCreatedAt: null,
        });
      },

      // Vector store management - simplified approach
      setThreadVectorStore: (threadId, vectorStoreId) => {
        set((state) => {
          // Update the current thread if it matches
          const updatedCurrentThread =
            state.currentThread?.id === threadId
              ? { ...state.currentThread, vector_store_id: vectorStoreId }
              : state.currentThread;

          // Update the threads array
          const updatedThreads = state.threads.map((thread) =>
            thread.id === threadId
              ? { ...thread, vector_store_id: vectorStoreId }
              : thread
          );

          return {
            currentThread: updatedCurrentThread,
            threads: updatedThreads,
          };
        });
      },

      getThreadVectorStore: (threadId) => {
        const state = get();

        // If no threadId provided, use current thread
        if (!threadId) {
          return state.currentThread?.vector_store_id || null;
        }

        // Check current thread first
        if (state.currentThread?.id === threadId) {
          return state.currentThread.vector_store_id || null;
        }

        // Check in threads array
        const thread = state.threads.find((t) => t.id === threadId);
        return thread?.vector_store_id || null;
      },

      // Get workspace vector store for workspace chats
      getWorkspaceVectorStore: (workspaceId) => {
        if (!workspaceId) {
          // Try to get from current workspace context
          const { useAuthStore } = require("@/store/authStore");
          const authState = useAuthStore.getState();
          const currentWorkspace = authState.currentWorkspace;

          if (currentWorkspace?.vector_store_id) {
            return currentWorkspace.vector_store_id;
          }

          return null;
        }

        // Check if current workspace matches
        const { useAuthStore } = require("@/store/authStore");
        const authState = useAuthStore.getState();
        const currentWorkspace = authState.currentWorkspace;

        if (
          currentWorkspace?.id === workspaceId &&
          currentWorkspace?.vector_store_id
        ) {
          return currentWorkspace.vector_store_id;
        }

        // Check in workspaces array
        const workspace = authState.workspaces?.find(
          (w) => w.id === workspaceId
        );
        return workspace?.vector_store_id || null;
      },

      ensureThreadVectorStore: async (
        threadId,
        isWorkspaceChat = false,
        workspaceId = null
      ) => {
        const state = get();

        // Use current thread if no threadId provided
        const targetThreadId = threadId || state.currentThreadId;
        if (!targetThreadId) {
          console.error("No thread ID provided for vector store creation");
          return null;
        }

        // For workspace chats, use workspace vector store instead of thread-specific one
        if (isWorkspaceChat && workspaceId) {
          console.log(
            `Ensuring workspace vector store for workspace: ${workspaceId}`
          );

          // Check if workspace already has a vector store
          const existingWorkspaceVectorStore =
            state.getWorkspaceVectorStore(workspaceId);
          if (existingWorkspaceVectorStore) {
            console.log(
              `Using existing workspace vector store: ${existingWorkspaceVectorStore}`
            );
            return existingWorkspaceVectorStore;
          }

          try {
            // Create workspace vector store
            const { createVectorStore } = await import(
              "@/lib/utils/vectorStoreUtils"
            );
            const { useAuthStore } = await import("@/store/authStore");

            const authState = useAuthStore.getState();
            const workspace =
              authState.workspaces?.find((w) => w.id === workspaceId) ||
              authState.currentWorkspace;
            const workspaceName = workspace?.name || `Workspace ${Date.now()}`;

            const vectorStoreId = await createVectorStore(
              `${workspaceName} Vector Store`
            );

            if (vectorStoreId) {
              // Update workspace in database
              try {
                await supabase
                  .from("workspaces")
                  .update({ vector_store_id: vectorStoreId })
                  .eq("id", workspaceId);

                // Update auth store
                useAuthStore.setState((authState) => {
                  const updatedWorkspaces =
                    authState.workspaces?.map((w) =>
                      w.id === workspaceId
                        ? { ...w, vector_store_id: vectorStoreId }
                        : w
                    ) || [];

                  const updatedCurrentWorkspace =
                    authState.currentWorkspace?.id === workspaceId
                      ? {
                          ...authState.currentWorkspace,
                          vector_store_id: vectorStoreId,
                        }
                      : authState.currentWorkspace;

                  return {
                    workspaces: updatedWorkspaces,
                    currentWorkspace: updatedCurrentWorkspace,
                  };
                });

                console.log(
                  `✅ Workspace vector store ${vectorStoreId} created and assigned to workspace ${workspaceId}`
                );
              } catch (dbError) {
                console.error(
                  "Error updating workspace with vector store ID:",
                  dbError
                );
              }

              return vectorStoreId;
            }
          } catch (error) {
            console.error("Error creating workspace vector store:", error);
            return null;
          }
        }

        // Regular thread vector store logic
        const existingVectorStoreId =
          state.getThreadVectorStore(targetThreadId);
        if (existingVectorStoreId) {
          console.log(
            `Using existing thread vector store: ${existingVectorStoreId} for thread: ${targetThreadId}`
          );
          return existingVectorStoreId;
        }

        try {
          // Create new thread vector store
          const { createVectorStore } = await import(
            "@/lib/utils/vectorStoreUtils"
          );
          const threadName = `Thread ${Date.now()}`;
          const vectorStoreId = await createVectorStore(threadName);

          if (vectorStoreId) {
            // Store in thread store
            state.setThreadVectorStore(targetThreadId, vectorStoreId);

            // Update database
            try {
              await supabase
                .from("threads")
                .update({ vector_store_id: vectorStoreId })
                .eq("id", targetThreadId);

              console.log(
                `✅ Thread vector store ${vectorStoreId} created and assigned to thread ${targetThreadId}`
              );
            } catch (dbError) {
              console.error(
                "Error updating thread with vector store ID:",
                dbError
              );
            }

            return vectorStoreId;
          }
        } catch (error) {
          console.error("Error creating thread vector store:", error);
          return null;
        }
      },

      // Thread context functions for file handling
      getThreadContext: (threadId) => {
        const state = get();
        if (!threadId) return null;

        // Return current thread context if it matches the requested thread
        if (state.currentThreadId === threadId) {
          return state.currentThreadContext || {};
        }

        // For other threads, return empty context (could be extended to cache contexts)
        return {};
      },

      setThreadContext: (context) => {
        set({ currentThreadContext: context });
      },
      setMessages: (messages) => {
        if (Array.isArray(messages) && messages.length === 0) {
          set({
            messages,
            messagesClearedAt: Date.now(),
            isMessagesExplicitlyCleared: true,
          });
        } else {
          set({ messages, isMessagesExplicitlyCleared: false });
        }
      },
      setThreads: (threads) => set({ threads }),
      setFilteredThreads: (filteredThreads) => set({ filteredThreads }),
      clearMessages: () => set({ messages: [] }),
      setThreadStoreLoading: (loading) => set({ isLoading: loading }),
      initialize: async () => {
        if (get().initialized) return;

        const { userId, currentWorkspaceId } = useAuthStore.getState();
        const {
          currentAssistantId,
          assistants,
          setCurrentAssistant,
          setCurrentAssistantId,
        } = useAssistantStore.getState();

        try {
          set({ initializing: true, error: null });

          // Validate required data
          if (!userId) {
            console.warn(
              "No user ID available during thread store initialization"
            );
            set({
              initialized: true,
              error: "User ID is not available. Please log in again.",
            });
            return;
          }

          // Set initial state with user context
          set({
            clients: { supabase: supabase },
            userId,
            currentWorkspaceId,
            currentAssistantId,
          });

          // If no assistant is selected but assistants exist, select the first one
          if (!currentAssistantId && assistants && assistants.length > 0) {
            const defaultAssistant = assistants[0];
            setCurrentAssistant(defaultAssistant);
            setCurrentAssistantId(defaultAssistant.id);
            set({ currentAssistantId: defaultAssistant.id });
          }

          try {
            // Check if automatic thread creation should be prevented
            const preventAutomaticCreation =
              localStorage.getItem("preventAutomaticThread") === "true";

            // Fetch user threads or create a new one if none exist
            // On initial load, don't set current thread - let navigation handle it
            const threads = await get().fetchUserThreads(userId, {
              preventAutomaticCreation,
              setCurrentThread: false, // Don't auto-select thread on initialization
            });

            // Clean up existing subscription channel if it exists
            const currentState = get();
            if (currentState.threadSubscriptionChannel) {
              console.log(
                "[ThreadStore] Cleaning up existing thread subscription channel"
              );
              supabase.removeChannel(currentState.threadSubscriptionChannel);
            }

            // Set up real-time subscription for thread deletions
            const channel = supabase
              .channel(`thread-deletions-${userId}-${Date.now()}`)
              .on(
                "postgres_changes",
                {
                  event: "DELETE",
                  schema: "public",
                  table: "threads",
                  filter: `user_id=eq.${userId}`,
                },
                (payload) => {
                  const deletedThreadId = payload.old?.id;
                  if (deletedThreadId) {
                    console.log(
                      `[ThreadStore] Thread ${deletedThreadId} deleted, removing from store`
                    );
                    const currentState = get();
                    const updatedThreads = currentState.threads.filter(
                      (t) => t.id !== deletedThreadId
                    );

                    // Clear current thread if it was deleted
                    const updates = {
                      threads: updatedThreads,
                    };

                    if (currentState.currentThreadId === deletedThreadId) {
                      updates.currentThread = null;
                      updates.currentThreadId = null;
                      updates.currentThreadTitle = null;
                      updates.messages = [];
                    }

                    set(updates);
                  }
                }
              )
              .subscribe();

            set({ initialized: true, threadSubscriptionChannel: channel });
          } catch (threadsError) {
            // Even if thread fetching fails, we still mark as initialized
            // This prevents infinite initialization loops
            set({
              initialized: true,
              error: `Failed to fetch threads: ${threadsError.message}`,
            });
          }
        } catch (error) {
          console.error("Thread store initialization error:", error);
          set({
            error: `Thread store initialization failed: ${error.message}`,
            initialized: true, // Still mark as initialized to prevent loops
          });
        } finally {
          set({ initializing: false });
        }
      },
      clearCurrentThread: () => {
        const state = get();
        const { threads: _threads, ...rest } = INITIAL_THREAD_STATE;

        // Check if current thread has an active stream
        const currentThreadId = state.currentThreadId;
        const hasActiveStream =
          currentThreadId && state.activeStreams?.has(currentThreadId);

        if (hasActiveStream) {
          // Preserve thread state if stream is active
          // Only clear non-streaming related state
          set({
            ...rest,
            currentThread: state.currentThread,
            currentThreadId: state.currentThreadId,
            currentThreadTitle: state.currentThreadTitle,
            threadMessages: state.threadMessages,
            activeStreams: state.activeStreams,
            // Clear UI state but preserve thread data
            ...INITIAL_STREAM_STATE,
            ...INITIAL_COMPUTED_STATE,
            ...INITIAL_TOOL_STATE,
            messages: [], // Clear messages array for UI, but threadMessages Map preserved
          });
        } else {
          // Normal clear when no active stream
          set({
            ...rest,
            ...INITIAL_MESSAGE_STATE,
            ...INITIAL_STREAM_STATE,
            ...INITIAL_COMPUTED_STATE,
            ...INITIAL_TOOL_STATE,
          });
        }
      },
      // Stream management methods
      registerStream: (threadId, messageId, abortController) => {
        if (!threadId || !messageId) {
          return false;
        }

        const state = get();
        const activeStreams = new Map(state.activeStreams || new Map());
        const currentThreadId = state.currentThreadId;

        // ENHANCED STREAM LIMIT ENFORCEMENT:
        // Foreground streams (active thread) bypass limit, background streams respect it
        const isForeground = threadId === currentThreadId;

        if (!isForeground && activeStreams.size >= state.maxConcurrentStreams) {
          // Find oldest background stream to abort
          let oldestStream = null;
          let oldestTime = Infinity;

          for (const [streamThreadId, stream] of activeStreams.entries()) {
            // Skip foreground streams when finding oldest
            if (streamThreadId === currentThreadId) {
              continue;
            }
            if (stream.startTime < oldestTime) {
              oldestTime = stream.startTime;
              oldestStream = { threadId: streamThreadId, ...stream };
            }
          }

          if (oldestStream) {
            // Abort the oldest background stream
            try {
              if (oldestStream.abortController) {
                oldestStream.abortController.abort(
                  "Stream cancelled due to concurrency limit"
                );
              }
              activeStreams.delete(oldestStream.threadId);

              // Show subtle toast notification
              if (typeof window !== "undefined") {
                const event = new CustomEvent("stream-limit-reached", {
                  detail: {
                    abortedThreadId: oldestStream.threadId,
                    abortedMessageId: oldestStream.messageId,
                    newThreadId: threadId,
                    newMessageId: messageId,
                  },
                });
                window.dispatchEvent(event);
              }

              if (import.meta.env.DEV) {
                console.log(
                  `[threadStore] Aborted oldest background stream for thread ${oldestStream.threadId} to make room for new stream`
                );
              }
            } catch (error) {
              console.error("Error aborting oldest background stream:", error);
              return false;
            }
          } else {
            // All streams are foreground - this shouldn't happen but return false to be safe
            console.warn(
              "[threadStore] Cannot register stream - all active streams are foreground"
            );
            return false;
          }
        }

        // Register the stream
        activeStreams.set(threadId, {
          threadId,
          messageId,
          startTime: Date.now(),
          abortController,
          isActive: true,
          isForeground,
        });

        set({ activeStreams });
        return true;
      },
      unregisterStream: (threadId, messageId) => {
        if (!threadId) {
          return false;
        }

        const state = get();
        const activeStreams = new Map(state.activeStreams || new Map());
        const stream = activeStreams.get(threadId);

        if (!stream) {
          return false;
        }

        // Verify messageId matches if provided
        if (messageId && stream.messageId !== messageId) {
          // Still unregister if threadId matches
        }

        activeStreams.delete(threadId);
        set({ activeStreams });

        // Emit stream completion event for notifications
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("stream-completed", {
              detail: { threadId, messageId: stream.messageId },
            })
          );
        }

        return true;
      },
      getActiveStreams: () => {
        const state = get();
        const activeStreams = state.activeStreams || new Map();
        return Array.from(activeStreams.values());
      },
      getActiveStreamCount: () => {
        const state = get();
        return (state.activeStreams || new Map()).size;
      },
      getStreamForThread: (threadId) => {
        if (!threadId) return null;
        const state = get();
        return state.activeStreams?.get(threadId) || null;
      },
      canStartNewStream: () => {
        const state = get();
        const activeCount = (state.activeStreams || new Map()).size;
        return activeCount < state.maxConcurrentStreams;
      },
      cancelStream: (threadId, messageId) => {
        if (!threadId) {
          return false;
        }

        const state = get();
        const activeStreams = new Map(state.activeStreams || new Map());
        const stream = activeStreams.get(threadId);

        if (!stream) {
          return false;
        }

        // Abort the stream if abortController exists
        if (stream.abortController) {
          try {
            stream.abortController.abort(
              "Stream cancelled due to concurrency limit"
            );
            console.log(`Aborted stream for thread ${threadId}`);
          } catch (error) {
            console.error("Error aborting stream for thread", threadId, error);
          }
        }

        activeStreams.delete(threadId);
        set({ activeStreams });

        return true;
      },
      // Optimistic message management methods
      addOptimisticMessage: (threadId, message) => {
        if (!threadId || !message) {
          return false;
        }

        const state = get();
        const optimisticMessages = new Map(
          state.optimisticMessages || new Map()
        );
        const threadMessages = optimisticMessages.get(threadId) || [];

        // Add the new optimistic message
        optimisticMessages.set(threadId, [...threadMessages, message]);
        set({ optimisticMessages });

        return true;
      },
      removeOptimisticMessage: (threadId, messageId) => {
        if (!threadId || !messageId) {
          return false;
        }

        const state = get();
        const optimisticMessages = new Map(
          state.optimisticMessages || new Map()
        );
        const threadMessages = optimisticMessages.get(threadId) || [];

        // Filter out the message with matching ID
        const filtered = threadMessages.filter((m) => m.id !== messageId);

        if (filtered.length === threadMessages.length) {
          return false;
        }

        optimisticMessages.set(threadId, filtered);
        set({ optimisticMessages });

        return true;
      },
      replaceOptimisticMessage: (threadId, tempId, realMessage) => {
        if (!threadId || !tempId || !realMessage) {
          return false;
        }

        const state = get();
        const optimisticMessages = new Map(
          state.optimisticMessages || new Map()
        );
        const threadMessages = optimisticMessages.get(threadId) || [];

        // Find and remove the optimistic message
        const index = threadMessages.findIndex((m) => m.id === tempId);

        if (index === -1) {
          return false;
        }

        // Remove the optimistic message (don't add real message here, it goes to messages array)
        threadMessages.splice(index, 1);
        optimisticMessages.set(threadId, threadMessages);
        set({ optimisticMessages });

        return true;
      },
      markOptimisticMessageFailed: (threadId, messageId, error) => {
        if (!threadId || !messageId) {
          return false;
        }

        const state = get();
        const optimisticMessages = new Map(
          state.optimisticMessages || new Map()
        );
        const threadMessages = optimisticMessages.get(threadId) || [];

        // Find the message and mark it as failed
        const index = threadMessages.findIndex((m) => m.id === messageId);

        if (index === -1) {
          return false;
        }

        // Update the message with error state
        threadMessages[index] = {
          ...threadMessages[index],
          isFailed: true,
          error: error?.message || String(error),
        };

        optimisticMessages.set(threadId, threadMessages);
        set({ optimisticMessages });

        return true;
      },
      getOptimisticMessages: (threadId) => {
        if (!threadId) return [];
        const state = get();
        return state.optimisticMessages?.get(threadId) || [];
      },
      findToolOutputById: (toolId) => {
        if (!toolId) return null;

        try {
          const state = get();
          const { toolOutputs, toolState } = state;

          // Check the dedicated toolOutputs Map first
          if (toolOutputs && toolOutputs.has(toolId)) {
            return toolOutputs.get(toolId);
          }

          // Check in toolState.outputs Map
          if (toolState?.outputs && toolState.outputs.has(toolId)) {
            return toolState.outputs.get(toolId);
          }

          // Check in active tools
          if (toolState?.active && toolState.active.has(toolId)) {
            const activeTool = toolState.active.get(toolId);
            if (activeTool?.output) {
              return activeTool.output;
            }
          }

          // Check for currentToolCall if ID matches
          if (
            state.currentToolCallId === toolId &&
            state.currentToolCallOutput
          ) {
            return state.currentToolCallOutput;
          }

          // Check in message metadata for any message that has this tool reference
          const toolRefMessage = state.messages.find(
            (msg) =>
              msg.metadata?.toolOutputRefId === toolId ||
              (msg.tool_calls && msg.tool_calls.some((tc) => tc.id === toolId))
          );

          if (toolRefMessage?.toolExecutions?.length > 0) {
            const toolExecution = toolRefMessage.toolExecutions.find(
              (exec) =>
                exec.toolCallId === toolId ||
                exec.openai_tool_call_id === toolId
            );
            if (toolExecution?.output) {
              return toolExecution.output;
            }
          }

          return null;
        } catch (error) {
          return null;
        }
      },
      updateMessageState: (msgData) => {
        if (typeof msgData === "object") {
          // RACE CONDITION FIX: Check if this message belongs to the current thread
          // BACKGROUND STREAMING FIX: Messages are stored in threadMessages Map for all threads
          // No queueing needed - messages will be loaded from Map when switching threads
          const currentState = get();
          const currentThreadId = currentState.currentThreadId;

          // Ensure content is a string
          const processedMsgData = { ...msgData };

          // CRITICAL FIX: Ensure created_at is ALWAYS set before any processing
          if (!processedMsgData.created_at) {
            // If we have a timestamp, use that
            if (processedMsgData.timestamp) {
              processedMsgData.created_at = processedMsgData.timestamp;
            } else {
              // Otherwise, use current time
              processedMsgData.created_at = new Date().toISOString();
            }
          }

          // CRITICAL FIX: Ensure updated_at is set
          if (!processedMsgData.updated_at) {
            processedMsgData.updated_at = new Date().toISOString();
          }

          // CRITICAL FIX: Ensure thread_id is preserved from input or current thread
          if (!processedMsgData.thread_id && processedMsgData.threadId) {
            processedMsgData.thread_id = processedMsgData.threadId;
          }
          if (!processedMsgData.thread_id && currentState.currentThreadId) {
            processedMsgData.thread_id = currentState.currentThreadId;
          }

          // BACKGROUND STREAMING FIX: Always update threadMessages Map for the target thread
          // This ensures messages are stored per-thread even when thread is not active
          const targetThreadId = processedMsgData.thread_id;
          if (targetThreadId) {
            // VALIDATION: Ensure message thread_id matches targetThreadId before storing
            if (processedMsgData.thread_id !== targetThreadId) {
              console.error(
                "[threadStore] Message thread_id mismatch - not storing in Map",
                {
                  messageId: processedMsgData.id,
                  messageThread: processedMsgData.thread_id,
                  targetThread: targetThreadId,
                }
              );
              return;
            }

            set((state) => {
              const threadMessagesMap = new Map(
                state.threadMessages || new Map()
              );
              const threadMessages =
                threadMessagesMap.get(targetThreadId) || [];

              // VALIDATION: Double-check all existing messages in this thread's Map entry
              const invalidMessages = threadMessages.filter(
                (msg) => msg.thread_id && msg.thread_id !== targetThreadId
              );
              if (invalidMessages.length > 0) {
                console.warn(
                  `[threadStore] Found ${invalidMessages.length} messages with wrong thread_id in Map for thread ${targetThreadId}`,
                  invalidMessages.map((m) => ({
                    id: m.id,
                    thread_id: m.thread_id,
                  }))
                );
              }

              // Find existing message or add new one
              const existingIndex = threadMessages.findIndex(
                (msg) => msg.id === processedMsgData.id
              );

              let updatedThreadMessages;
              if (existingIndex !== -1) {
                // Update existing message
                updatedThreadMessages = [...threadMessages];
                const existingMessage = threadMessages[existingIndex];
                updatedThreadMessages[existingIndex] = {
                  ...existingMessage,
                  ...processedMsgData,
                  created_at:
                    existingMessage.created_at || processedMsgData.created_at,
                  updated_at:
                    processedMsgData.updated_at || new Date().toISOString(),
                };
              } else if (processedMsgData.id) {
                // Add new message
                updatedThreadMessages = [
                  ...threadMessages,
                  {
                    ...processedMsgData,
                    created_at:
                      processedMsgData.created_at || new Date().toISOString(),
                    updated_at:
                      processedMsgData.updated_at || new Date().toISOString(),
                  },
                ];
              } else {
                updatedThreadMessages = threadMessages;
              }

              // Sort messages by created_at
              if (updatedThreadMessages.length > 0) {
                updatedThreadMessages.sort((a, b) => {
                  const dateA = new Date(
                    a.created_at || a.timestamp || "1970-01-01T00:00:00.000Z"
                  );
                  const dateB = new Date(
                    b.created_at || b.timestamp || "1970-01-01T00:00:00.000Z"
                  );
                  if (dateA.getTime() === dateB.getTime()) {
                    return (a.id || "").localeCompare(b.id || "");
                  }
                  return dateA - dateB;
                });
              }

              threadMessagesMap.set(targetThreadId, updatedThreadMessages);

              return { threadMessages: threadMessagesMap };
            });
          }

          // Handle content that might be an object
          if (
            processedMsgData.content &&
            typeof processedMsgData.content === "object"
          ) {
            // If content is an array of objects with text property
            if (Array.isArray(processedMsgData.content)) {
              processedMsgData.content = processedMsgData.content.reduce(
                (acc, item) => {
                  if (item.type === "text") {
                    if (typeof item.text === "string") return acc + item.text;
                    if (item.text?.value) return acc + item.text.value;
                  }
                  return acc;
                },
                ""
              );
            } else {
              // If content is a single object
              processedMsgData.content =
                processedMsgData.content?.text?.value ||
                processedMsgData.content?.text ||
                JSON.stringify(processedMsgData.content);
            }
          }

          // IMPORTANT: Skip database operations for streaming messages
          // Add skipDatabaseSave property to explicitly mark messages that shouldn't be saved yet
          if (
            processedMsgData.isStreaming === true ||
            processedMsgData.skipDatabaseSave === true
          ) {
            processedMsgData.skipDatabaseSave = true;
          }

          // Check if the message already exists to update it
          // instead of adding a duplicate
          // BACKGROUND STREAMING FIX: Only update messages array if thread is currently active
          // CRITICAL: Don't update messages array if currentThreadId is null/undefined (e.g., on /chat/new)
          // ENHANCED: Explicit null check to prevent any updates when on /chat/new
          const isActiveThread =
            targetThreadId &&
            currentState.currentThreadId !== null &&
            currentState.currentThreadId !== undefined &&
            targetThreadId === currentState.currentThreadId;
          if (processedMsgData.id && isActiveThread) {
            set((state) => {
              // SAFETY CHECK: Ensure state.messages exists and is an array
              if (!state.messages || !Array.isArray(state.messages)) {
                return { messages: [processedMsgData] };
              }

              // Don't use find and indexOf in a loop - check once and use the result
              const existingMsgIndex = state.messages.findIndex(
                (msg) => msg.id === processedMsgData.id
              );

              // If message exists, update it instead of adding a new one
              if (existingMsgIndex !== -1) {
                // Create a new array to maintain immutability
                const updatedMessages = [...state.messages];

                // CRITICAL FIX: Preserve the original created_at when updating existing messages
                const existingMessage = state.messages[existingMsgIndex];
                const preservedCreatedAt =
                  existingMessage.created_at || processedMsgData.created_at;

                // Update the existing message, preserving any fields not in the update
                updatedMessages[existingMsgIndex] = {
                  ...existingMessage,
                  ...processedMsgData,
                  // CRITICAL: Always preserve the original created_at timestamp
                  created_at: preservedCreatedAt,
                  // Update the updated_at timestamp
                  updated_at:
                    processedMsgData.updated_at || new Date().toISOString(),
                  // CRITICAL FIX: Ensure thread_id is always set for database consistency
                  thread_id:
                    processedMsgData.thread_id ||
                    existingMessage.thread_id ||
                    currentState.currentThreadId,
                };

                // CRITICAL: Sort messages by created_at to maintain chronological order
                // Use a stable sort that handles edge cases properly
                updatedMessages.sort((a, b) => {
                  const dateA = new Date(
                    a.created_at || a.timestamp || "1970-01-01T00:00:00.000Z"
                  );
                  const dateB = new Date(
                    b.created_at || b.timestamp || "1970-01-01T00:00:00.000Z"
                  );

                  // If timestamps are identical, use message ID for stable sorting
                  if (dateA.getTime() === dateB.getTime()) {
                    return a.id.localeCompare(b.id);
                  }

                  return dateA - dateB;
                });

                return { messages: updatedMessages };
              }

              // If message doesn't exist, add it
              // CRITICAL FIX: Ensure new messages have thread_id set
              const messageWithThreadId = {
                ...processedMsgData,
                thread_id:
                  processedMsgData.thread_id || currentState.currentThreadId,
              };
              const newMessages = [...state.messages, messageWithThreadId];

              // CRITICAL: Sort messages by created_at to maintain chronological order
              // Use a stable sort that handles edge cases properly
              newMessages.sort((a, b) => {
                const dateA = new Date(
                  a.created_at || a.timestamp || "1970-01-01T00:00:00.000Z"
                );
                const dateB = new Date(
                  b.created_at || b.timestamp || "1970-01-01T00:00:00.000Z"
                );

                // If timestamps are identical, use message ID for stable sorting
                if (dateA.getTime() === dateB.getTime()) {
                  return a.id.localeCompare(b.id);
                }

                return dateA - dateB;
              });

              return { messages: newMessages };
            });
          } else if (
            !processedMsgData.id &&
            isActiveThread &&
            currentState.currentThreadId
          ) {
            // No ID provided, just append as before (only for active thread)
            // CRITICAL: Also check currentThreadId exists to prevent updates on /chat/new
            set((state) => {
              // SAFETY CHECK: Ensure state.messages exists and is an array
              if (!state.messages || !Array.isArray(state.messages)) {
                return { messages: [processedMsgData] };
              }

              const newMessages = [...state.messages, processedMsgData];

              // CRITICAL: Sort messages by created_at to maintain chronological order
              // Use a stable sort that handles edge cases properly
              newMessages.sort((a, b) => {
                const dateA = new Date(
                  a.created_at || a.timestamp || "1970-01-01T00:00:00.000Z"
                );
                const dateB = new Date(
                  b.created_at || b.timestamp || "1970-01-01T00:00:00.000Z"
                );

                // If timestamps are identical, use message ID for stable sorting
                if (dateA.getTime() === dateB.getTime()) {
                  return a.id.localeCompare(b.id);
                }

                return dateA - dateB;
              });

              return { messages: newMessages };
            });
          }
        } else if (Array.isArray(msgData)) {
          // When we receive an array, this is a complete replacement of messages
          // CRITICAL FIX: Before replacing, preserve any user messages that might be missing
          const currentState = get();
          const currentMessages = currentState.messages || [];
          const existingUserMessages = currentMessages.filter(
            (msg) => msg.role === "user"
          );

          // Check if any user messages are missing from the new array
          const incomingUserMessages = msgData.filter(
            (msg) => msg && msg.role === "user"
          );
          const missingUserMessages = existingUserMessages.filter(
            (userMsg) =>
              !msgData.some((newMsg) => newMsg && newMsg.id === userMsg.id)
          );

          if (missingUserMessages.length > 0) {
            console.warn(
              `🔒 Preserving ${missingUserMessages.length} user messages during array replacement`
            );
            // Add missing user messages to the array
            msgData = [...msgData, ...missingUserMessages];
          }

          // Process each message in the array for content formatting
          const processedMessages = msgData.map((msg) => {
            if (msg && typeof msg === "object") {
              const processedMsg = { ...msg };

              // CRITICAL FIX: Ensure created_at is ALWAYS set for array messages too
              if (!processedMsg.created_at) {
                // If we have a timestamp, use that
                if (processedMsg.timestamp) {
                  processedMsg.created_at = processedMsg.timestamp;
                } else {
                  // Otherwise, use current time
                  processedMsg.created_at = new Date().toISOString();
                }
              }

              // CRITICAL FIX: Ensure updated_at is set
              if (!processedMsg.updated_at) {
                processedMsg.updated_at =
                  processedMsg.created_at || new Date().toISOString();
              }

              // CRITICAL FIX: Ensure thread_id is set for array messages
              if (!processedMsg.thread_id && processedMsg.threadId) {
                processedMsg.thread_id = processedMsg.threadId;
              }
              if (!processedMsg.thread_id && currentState.currentThreadId) {
                processedMsg.thread_id = currentState.currentThreadId;
              }

              // Handle content that might be an object
              if (
                processedMsg.content &&
                typeof processedMsg.content === "object"
              ) {
                // If content is an array of objects with text property
                if (Array.isArray(processedMsg.content)) {
                  processedMsg.content = processedMsg.content.reduce(
                    (acc, item) => {
                      if (item.type === "text") {
                        if (typeof item.text === "string")
                          return acc + item.text;
                        if (item.text?.value) return acc + item.text.value;
                      }
                      return acc;
                    },
                    ""
                  );
                } else {
                  // If content is a single object
                  processedMsg.content =
                    processedMsg.content?.text?.value ||
                    processedMsg.content?.text ||
                    JSON.stringify(processedMsg.content);
                }
              }

              // IMPORTANT: Skip database operations for streaming messages
              if (
                processedMsg.isStreaming === true ||
                processedMsg.skipDatabaseSave === true
              ) {
                processedMsg.skipDatabaseSave = true;
              }

              return processedMsg;
            }
            return msg;
          });

          // CRITICAL: Sort messages by created_at to maintain chronological order
          // Use a stable sort that handles edge cases properly
          processedMessages.sort((a, b) => {
            const dateA = new Date(
              a.created_at || a.timestamp || "1970-01-01T00:00:00.000Z"
            );
            const dateB = new Date(
              b.created_at || b.timestamp || "1970-01-01T00:00:00.000Z"
            );

            // If timestamps are identical, use message ID for stable sorting
            if (dateA.getTime() === dateB.getTime()) {
              return a.id.localeCompare(b.id);
            }

            return dateA - dateB;
          });

          // Completely replace the messages array with the new one
          set({ messages: processedMessages });
        } else {
          console.error("Invalid message data:", msgData);
        }
      },
      /**
       * Update only the metadata of a specific message without replacing other fields
       * Used for adding extracted memories, analytics data, etc.
       * @param {string} threadId - The thread containing the message
       * @param {string} messageId - The ID of the message to update
       * @param {object} metadata - The metadata to merge into the existing metadata
       */
      updateMessageMetadata: (threadId, messageId, metadata) => {
        if (!threadId || !messageId || !metadata) {
          console.warn(
            "[threadStore] updateMessageMetadata: missing required params",
            {
              threadId,
              messageId,
              hasMetadata: !!metadata,
            }
          );
          return;
        }

        set((state) => {
          // Update in threadMessages Map
          const threadMessagesMap = new Map(state.threadMessages || new Map());
          const threadMessages = threadMessagesMap.get(threadId) || [];

          const messageIndex = threadMessages.findIndex(
            (m) => m.id === messageId
          );
          if (messageIndex !== -1) {
            const existingMessage = threadMessages[messageIndex];
            const updatedMessages = [...threadMessages];
            updatedMessages[messageIndex] = {
              ...existingMessage,
              metadata: {
                ...(existingMessage.metadata || {}),
                ...metadata,
              },
              updated_at: new Date().toISOString(),
            };
            threadMessagesMap.set(threadId, updatedMessages);
          }

          // Also update in current messages array if this is the active thread
          const isActiveThread = state.currentThreadId === threadId;
          let updatedCurrentMessages = state.messages;

          if (isActiveThread && state.messages) {
            const currentMsgIndex = state.messages.findIndex(
              (m) => m.id === messageId
            );
            if (currentMsgIndex !== -1) {
              const existingMsg = state.messages[currentMsgIndex];
              updatedCurrentMessages = [...state.messages];
              updatedCurrentMessages[currentMsgIndex] = {
                ...existingMsg,
                metadata: {
                  ...(existingMsg.metadata || {}),
                  ...metadata,
                },
                updated_at: new Date().toISOString(),
              };
            }
          }

          return {
            threadMessages: threadMessagesMap,
            ...(isActiveThread ? { messages: updatedCurrentMessages } : {}),
          };
        });

        console.log(
          `[threadStore] Updated metadata for message ${messageId} in thread ${threadId}`
        );

        // Persist to database (non-blocking)
        (async () => {
          try {
            // Try fetching by UUID 'id' first, then by text 'message_id' as fallback
            let existingMessage = null;

            // First try by UUID id
            const { data: dataById, error: errorById } = await supabase
              .from("thread_messages")
              .select("id, metadata")
              .eq("id", messageId)
              .single();

            if (!errorById && dataById) {
              existingMessage = dataById;
            } else {
              // Fallback: try by message_id (text field)
              const { data: dataByMsgId, error: errorByMsgId } = await supabase
                .from("thread_messages")
                .select("id, metadata")
                .eq("message_id", messageId)
                .single();

              if (!errorByMsgId && dataByMsgId) {
                existingMessage = dataByMsgId;
              } else {
                console.error(
                  "[threadStore] Failed to find message for metadata update:",
                  { messageId, errorById, errorByMsgId }
                );
                return;
              }
            }

            // Merge with existing metadata
            const mergedMetadata = {
              ...(existingMessage?.metadata || {}),
              ...metadata,
            };

            // Use the actual UUID id for the update
            const actualId = existingMessage.id;
            const { error: updateError } = await supabase
              .from("thread_messages")
              .update({
                metadata: mergedMetadata,
                updated_at: new Date().toISOString(),
              })
              .eq("id", actualId);

            if (updateError) {
              console.error(
                "[threadStore] Failed to persist message metadata:",
                updateError
              );
            } else {
              console.log(
                `[threadStore] Persisted metadata for message ${actualId}`
              );
            }
          } catch (err) {
            console.error(
              "[threadStore] Error persisting message metadata:",
              err
            );
          }
        })();
      },
      fetchUserThreads: async (
        userId,
        { preventAutomaticCreation = false, setCurrentThread = true } = {}
      ) => {
        try {
          set({ isLoading: true, error: null });
          if (!userId) {
            console.error("fetchUserThreads: User ID is required");
            throw new Error("User ID is required");
          }

          // Check if we have an assistant selected - try to set one if available
          // Note: Assistants are only strictly required for creating new threads, not fetching existing ones
          const {
            currentAssistantId,
            assistants,
            setCurrentAssistant,
            setCurrentAssistantId,
          } = useAssistantStore.getState();
          if (!currentAssistantId && assistants && assistants.length > 0) {
            const defaultAssistant = assistants[0];
            setCurrentAssistant(defaultAssistant);
            setCurrentAssistantId(defaultAssistant.id);
            set({ currentAssistantId: defaultAssistant.id });
          }
          // Note: We no longer throw an error here - threads can be fetched without an assistant
          // The assistant requirement is checked later only when creating a new thread

          // 1. Get threads with complete data from Supabase
          const { data: threads, error: dbError } = await supabase
            .from("threads")
            .select(
              `
        id,
        thread_id,
        openai_thread_id,
        user_id,
        workspace_id,
        title,
        message_count,
        metadata,
        tool_resources,
        created_at,
        updated_at,
        last_message_at
      `
            )
            .eq("user_id", userId)
            .neq("status", "deleted")
            .order("last_message_at", { ascending: false })
            .order("created_at", { ascending: false });

          if (dbError) {
            console.error("Database query error:", dbError);
            throw dbError;
          }

          // Check if user has no threads and create one if needed
          if (!threads || threads.length === 0) {
            // Check if automatic thread creation should be prevented (set during onboarding)
            // Either use the passed parameter or check localStorage directly
            const shouldPreventAutomaticThread =
              preventAutomaticCreation ||
              localStorage.getItem("preventAutomaticThread") === "true";

            if (shouldPreventAutomaticThread) {
              // Only remove from localStorage if that's where we got the flag from
              if (localStorage.getItem("preventAutomaticThread") === "true") {
                localStorage.removeItem("preventAutomaticThread"); // Clear the flag
              }
              set({
                threads: [],
                currentThread: null,
                currentThreadId: null,
                isLoading: false,
              });
              return []; // Return empty threads array
            }

            // Check if we have an assistant before trying to create a thread
            const {
              currentAssistantId: assistantId,
              assistants: availableAssistants,
            } = useAssistantStore.getState();
            if (
              !assistantId &&
              (!availableAssistants || availableAssistants.length === 0)
            ) {
              // No assistants available - can't create a thread, return empty
              set({
                threads: [],
                currentThread: null,
                currentThreadId: null,
                isLoading: false,
              });
              return [];
            }

            try {
              // Create a new thread for the user
              const newThread = await get().createThread();

              // Initialize store with the new thread
              set({
                threads: [newThread],
                currentThread: newThread,
                currentThreadId: newThread.id,
                currentThreadTitle: newThread.title,
                isLoading: false,
                error: null,
              });

              return [newThread];
            } catch (createError) {
              set({
                isLoading: false,
                error: `Failed to create initial thread: ${createError.message}`,
                threads: [],
                currentThread: null,
              });
              return [];
            }
          }

          // 2. Format threads with all data
          const formattedThreads = (threads || []).map((thread) => ({
            id: thread.id,
            thread_id: thread.thread_id,
            openai_thread_id: thread.openai_thread_id,
            user_id: thread.user_id,
            workspace_id: thread.workspace_id,
            title: thread.title || "Untitled Thread",
            message_count: parseInt(thread.message_count || "0", 10),
            last_message_at:
              thread.last_message_at || thread.updated_at || thread.created_at,
            metadata: {
              ...thread.metadata,
              thread_title: thread.title || "Untitled Thread",
              message_count: String(thread.message_count || 0),
              created_at: thread.created_at,
              updated_at: thread.updated_at,
              synced_at: thread.metadata?.synced_at || new Date().toISOString(),
            },
            tool_resources: thread.tool_resources || {},
            created_at: thread.created_at || new Date().toISOString(),
            updated_at:
              thread.updated_at ||
              thread.created_at ||
              new Date().toISOString(),
          }));

          // Filter out threads with "New Chat" titles during initialization
          const filteredThreads = formattedThreads.filter(
            (thread) => thread.title !== "New Chat"
          );

          // Create a map to store messages by thread ID
          const threadMessagesMap = new Map();

          // We previously fetched messages for all threads here, but now we'll skip that
          // to improve performance. Messages will be loaded on-demand when a thread is selected.

          // We still need to initialize the message map with empty arrays
          if (filteredThreads.length > 0) {
            filteredThreads.forEach((thread) => {
              threadMessagesMap.set(thread.id, []);
            });
          }

          // Get the first thread ID to use later
          const firstThreadId =
            filteredThreads.length > 0 ? filteredThreads[0].id : null;

          // Update store with the filtered threads and empty message map
          // Only set current thread if setCurrentThread is true (not during initial load)
          const storeUpdate = {
            threads: filteredThreads,
            threadMessages: threadMessagesMap,
            messages: [], // Start with empty messages array instead of loading first thread messages
          };

          if (setCurrentThread && filteredThreads.length > 0) {
            // Set current thread only if explicitly requested (not during initial load)
            storeUpdate.currentThread = filteredThreads[0];
            storeUpdate.currentThreadId = filteredThreads[0].id;
            storeUpdate.currentThreadTitle = filteredThreads[0].title;
          } else {
            // On initial load, don't set current thread - let navigation handle it
            storeUpdate.currentThread = null;
            storeUpdate.currentThreadId = null;
            storeUpdate.currentThreadTitle = null;
          }

          set(storeUpdate);

          // If we have a first thread and we set it as current, set up a deferred load of its messages
          // This allows the UI to render quickly but still show messages soon after
          if (firstThreadId && setCurrentThread) {
            // Use setTimeout with 0ms to defer execution until after the current call stack clears
            setTimeout(() => {
              // Only load messages if this is still the current thread
              if (get().currentThreadId === firstThreadId) {
                get().getThreadMessages(firstThreadId);
              }
            }, 0);
          }

          // Return the filtered threads
          return filteredThreads;
        } catch (error) {
          console.error("Error fetching user threads:", error);
          set({
            isLoading: false,
            error: error.message || "Failed to fetch threads",
            threads: [],
            currentThread: null,
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      createThread: async (
        userId,
        assistantId,
        title = "New Chat",
        initialContext = "",
        metadata = {}
      ) => {
        try {
          set({ isLoading: true, error: null });

          // Check if this should be a Supabase-only thread
          const isSupabaseOnlyThread =
            metadata?.supabaseOnly === true ||
            localStorage.getItem("createSupabaseOnlyThread") === "true";

          // Check if we should force a fresh thread creation
          const forceNewThread =
            localStorage.getItem("forceNewThread") === "true";

          // Check if we should clear current thread state after creation
          const clearCurrentThreadAfterCreate =
            localStorage.getItem("clearCurrentThreadAfterCreate") === "true";

          // Clear the localStorage flags if they exist
          if (localStorage.getItem("createSupabaseOnlyThread") === "true") {
            localStorage.removeItem("createSupabaseOnlyThread");
          }

          if (localStorage.getItem("forceNewThread") === "true") {
            localStorage.removeItem("forceNewThread");
          }

          if (
            localStorage.getItem("clearCurrentThreadAfterCreate") === "true"
          ) {
            localStorage.removeItem("clearCurrentThreadAfterCreate");
          }

          // OPTIMISTIC RENDERING FIX: Use provided thread ID if available, otherwise generate new one
          const threadId = metadata?.id || fileUtils.generateUUID();
          const currentTime = new Date().toISOString();

          // Get current provider
          const currentProvider = getCurrentProvider();

          // Add workspace information to metadata
          // CRITICAL FIX: Only use workspace_id if explicitly provided in metadata AND this is a workspace chat
          // This prevents regular chats from being incorrectly associated with workspaces
          const isWorkspaceChat =
            metadata?.from_workspace_chat === true ||
            metadata?.workspace_initiated === true ||
            metadata?.is_workspace_chat === true ||
            metadata?.workspace_origin === true;
          const workspaceId = isWorkspaceChat
            ? metadata?.workspace_id || null
            : null;

          // Determine if this is a team workspace thread
          // Check multiple sources to determine if this should be a team thread
          let isTeamThread = false;

          // First check if explicitly set in metadata
          if (
            metadata?.is_team === true ||
            metadata?.is_shared_thread === true
          ) {
            isTeamThread = true;
          }

          // Check if workspace context indicates this is a team workspace
          if (workspaceId && !isTeamThread) {
            try {
              // Get workspace data from auth store first (faster)
              const { workspaces, currentWorkspace } = useAuthStore.getState();

              // First try to find workspace in the workspaces array
              let workspace = workspaces?.find((w) => w.id === workspaceId);

              // If not found in workspaces array, use currentWorkspace from auth store
              if (!workspace && currentWorkspace?.id === workspaceId) {
                workspace = currentWorkspace;
              }

              // If still not found, try to fetch from database directly
              if (!workspace) {
                try {
                  const { data: dbWorkspace, error } = await supabase
                    .from("workspaces")
                    .select("id, name, is_team")
                    .eq("id", workspaceId)
                    .single();

                  if (!error && dbWorkspace) {
                    workspace = dbWorkspace;
                  } else {
                    console.warn(error);
                  }
                } catch (dbError) {
                  console.warn(dbError);
                }
              }

              if (workspace?.is_team === true) {
                isTeamThread = true;
              } else if (workspace?.is_team === false) {
                // Explicitly set to false for personal workspaces
                isTeamThread = false;
              } else if (!workspace) {
                // If not found in auth store, check localStorage for workspace context
                const pendingWorkspaceChat = localStorage.getItem(
                  "pendingWorkspaceChat"
                );
                if (pendingWorkspaceChat === workspaceId) {
                  // Check if we have workspace type info in localStorage
                  const pendingWorkspaceType = localStorage.getItem(
                    "pendingWorkspaceType"
                  );
                  if (pendingWorkspaceType === "team") {
                    isTeamThread = true;
                  } else {
                    // Default to false for unknown workspace types
                    isTeamThread = false;
                  }
                }
              }
            } catch (error) {
              console.warn("Error checking workspace team status:", error);
              // Default to false on error
              isTeamThread = false;
            }
          }

          // Enhanced metadata
          const enhancedMetadata = {
            ...metadata,
            provider: currentProvider,
            created_at: currentTime,
            updated_at: currentTime,
            thread_title: title,
            initial_context: initialContext,
            user_id: userId,
            assistant_id: assistantId,
            workspace_id: workspaceId,
            supabaseOnly: isSupabaseOnlyThread,
            forceNewThread: forceNewThread,
            is_team: isTeamThread, // Add to metadata as well
            // Add this flag for workspace-created threads
            needs_title_generation:
              localStorage.getItem("needs_title_generation") === "true",
          };

          // Prepare thread data
          const threadData = {
            id: threadId,
            title: title,
            user_id: userId,
            assistant_id: assistantId,
            workspace_id: workspaceId,
            created_at: currentTime,
            updated_at: currentTime,
            last_message_at: currentTime,
            message_count: 0,
            metadata: enhancedMetadata,
            provider: currentProvider,
            status: "active",
            // CRITICAL: Set the is_team field for team workspace threads
            is_team: isTeamThread,
            is_shared: isTeamThread, // Also set is_shared for team threads
          };

          // Add provider-specific thread ID if available
          if (metadata.provider_thread_id) {
            threadData.provider_thread_id = metadata.provider_thread_id;
          }

          // For OpenAI compatibility, ensure openai_thread_id exists but can be null
          if (
            currentProvider === "openai" &&
            metadata.openai_thread_id &&
            !isSupabaseOnlyThread
          ) {
            threadData.openai_thread_id = metadata.openai_thread_id;
          } else {
            // For non-OpenAI providers or Supabase-only threads, use a placeholder
            threadData.openai_thread_id = `placeholder_${threadId}`;
          }

          // Insert thread into Supabase
          const { data: thread, error } = await supabase
            .from("threads")
            .insert(threadData)
            .select()
            .single();

          if (error) {
            console.error("Error creating thread in Supabase:", error);
            throw new Error(`Failed to create thread: ${error.message}`);
          }

          // If this is the first message and there's initial context, create a system message
          if (initialContext && initialContext.length > 0) {
            try {
              const systemMessageId = fileUtils.generateUUID();
              const systemMessageData = {
                thread_id: threadId,
                message_id: systemMessageId,
                role: "system",
                content: initialContext,
                created_at: currentTime,
                updated_at: currentTime,
                user_id: userId,
                workspace_id: workspaceId,
                metadata: {
                  is_initial_context: true,
                  thread_id: threadId,
                  created_at: currentTime,
                },
                provider: currentProvider,
              };

              // Add the system message to Supabase
              await supabase.from("thread_messages").insert(systemMessageData);
            } catch (msgError) {
              console.error("Error creating initial system message:", msgError);
              // Continue even if system message creation fails
            }
          }

          // If we need to clear the current thread state, do it before setting new thread
          if (clearCurrentThreadAfterCreate) {
            // Call clearCurrentThread first
            get().clearCurrentThread();
          }

          // Format the created thread
          const formattedThread = {
            id: thread.id,
            title: thread.title,
            created_at: thread.created_at,
            updated_at: thread.updated_at,
            last_message_at: thread.last_message_at,
            message_count: thread.message_count,
            metadata: thread.metadata || {},
            provider: thread.provider,
            provider_thread_id: thread.provider_thread_id,
            openai_thread_id: thread.openai_thread_id,
            user_id: thread.user_id,
            assistant_id: thread.assistant_id,
            workspace_id: thread.workspace_id,
            is_team: thread.is_team, // Include is_team in formatted thread
            is_shared: thread.is_shared, // Include is_shared in formatted thread
          };

          // Update the store with the new thread
          // CRITICAL FIX: Filter out any existing optimistic thread with the same ID to prevent duplicates
          set((state) => ({
            threads: [
              formattedThread,
              ...state.threads.filter(
                (t) =>
                  t.id !== formattedThread.id ||
                  (t.id === formattedThread.id && !t.metadata?.isOptimistic)
              ),
            ].filter(
              (thread, index, self) =>
                index === self.findIndex((t) => t.id === thread.id)
            ), // Additional deduplication by ID
            currentThread: formattedThread,
            currentThreadId: formattedThread.id,
            currentThreadTitle: formattedThread.title,
            isLoading: false,
            // Ensure messages array is reset to empty for the new thread
            messages: clearCurrentThreadAfterCreate ? [] : state.messages,
          }));

          // Store the new thread ID in localStorage for navigation
          // localStorage.setItem("newlyCreatedThreadId", thread.id);

          return formattedThread;
        } catch (error) {
          console.error("Error in createThread:", error);
          set({ error: error.message, isLoading: false });
          return null;
        }
      },

      getThreadMessages: async (threadId, { forceRefresh = false } = {}) => {
        const currentState = get();
        const {
          currentThreadId,
          messages,
          threadMessages,
          preventNewMessageClear,
        } = currentState;

        // Add logging to track thread changes

        // Set loading state immediately when changing threads
        if (threadId !== currentThreadId) {
          set({ isLoading: true });

          // IMPORTANT: Clear messages when switching threads to prevent cross-thread contamination
          // Only do this when thread ID actually changes
          set({ messages: [] });
        }

        try {
          // CRITICAL CHANGE: Always force a refresh when threadSwitchInProgress is true
          // This ensures messages are loaded even during thread switching
          const threadSwitchInProgress = currentState.threadSwitchInProgress;
          if (threadSwitchInProgress) {
            forceRefresh = true;
          }

          // Get thread from cache - it should already be fetched by fetchThread before this is called
          // If not in cache, fetch it now
          const threads = get().threads || [];
          let thread = threads.find((t) => t.id === threadId);

          if (!thread) {
            // Thread not in cache, fetch it
            const { data: fetchedThread, error: threadError } = await supabase
              .from("threads")
              .select("*")
              .eq("id", threadId)
              .single();

            if (threadError || !fetchedThread) {
              console.error("Error fetching thread:", threadError);
              set({
                isLoading: false,
                error: `Thread not found: ${threadError?.message || "Thread does not exist"}`,
              });
              throw new Error(
                `Thread not found: ${threadError?.message || "Thread does not exist"}`
              );
            }

            thread = fetchedThread;

            // Add to cache
            set((state) => ({
              threads: [
                ...state.threads.filter((t) => t.id !== threadId),
                thread,
              ],
            }));
          }

          // Fetch messages from Supabase using thread_messages table

          const { data: supabaseMessages, error: messagesError } =
            await supabase
              .from("thread_messages")
              .select("*")
              .eq("thread_id", threadId)
              .order("created_at", { ascending: true });

          if (messagesError) {
            console.error("Error fetching messages:", messagesError);
            set({
              isLoading: false,
              error: `Failed to fetch messages: ${messagesError.message}`,
            });
            throw new Error(
              `Failed to fetch messages: ${messagesError.message}`
            );
          }

          // Process and format messages
          let formattedMessages = supabaseMessages.map((message) => ({
            id: message.message_id,
            threadId: message.thread_id,
            role: message.role,
            content: message.content,
            created_at: message.created_at,
            updated_at: message.updated_at || message.created_at,
            metadata: message.metadata || {},
            attachments: message.attachments || [],
            annotations: message.annotations || [],
            file_ids: message.file_ids || [],
            tool_calls: message.tool_calls || [],
            provider: message.provider || "openai",
            provider_message_id: message.provider_message_id,
            message_index: supabaseMessages.indexOf(message),
            isStreamComplete: true,
            messageStatus: "completed",
          }));

          // Ensure messages are sorted by created_at in ascending order (oldest first)
          formattedMessages.sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return dateA - dateB;
          });

          // Check for pending message in localStorage for first message in new thread
          let pendingMessage = null;
          try {
            const storedPendingMessage = localStorage.getItem(
              "pendingMessageForThread"
            );
            if (storedPendingMessage) {
              const pendingData = JSON.parse(storedPendingMessage);

              // Verify this pending message belongs to this thread
              if (
                pendingData.threadId === threadId ||
                pendingData.thread_id === threadId
              ) {
                // Check if message already exists in formatted messages
                const messageExists = formattedMessages.some(
                  (msg) => msg.id === pendingData.messageId
                );

                if (!messageExists) {
                  pendingMessage = {
                    id: pendingData.messageId,
                    threadId: threadId,
                    thread_id: threadId, // Ensure consistent format
                    role: "user",
                    content: pendingData.content,
                    created_at:
                      pendingData.timestamp || new Date().toISOString(),
                    updated_at:
                      pendingData.timestamp || new Date().toISOString(),
                    metadata: {},
                    message_index: formattedMessages.length,
                    isStreamComplete: true,
                    messageStatus: "completed",
                  };

                  // Clear pending message from localStorage once used
                  localStorage.removeItem("pendingMessageForThread");
                }
              }
            }
          } catch (localStorageError) {
            console.error(localStorageError);
            // Continue without the pending message
          }

          // NEW: Check for optimistic workspace message
          let optimisticMessage = null;
          try {
            // Check for optimistic workspace message from store or localStorage
            const pendingWorkspaceMsg = currentState.pendingWorkspaceMessage;
            const storedOptimisticMessage = localStorage.getItem(
              "optimisticWorkspaceMessage"
            );

            if (
              (pendingWorkspaceMsg || storedOptimisticMessage) &&
              formattedMessages.length === 0
            ) {
              // Check if this is a new thread that might be from workspace
              const isWorkspaceThread =
                thread.metadata?.from_workspace_chat ||
                thread.metadata?.workspace_initiated ||
                thread.workspace_id;

              if (isWorkspaceThread) {
                // Use the pending message from store if available, otherwise from localStorage
                let optimisticData = pendingWorkspaceMsg;
                if (!optimisticData && storedOptimisticMessage) {
                  try {
                    optimisticData = JSON.parse(storedOptimisticMessage);
                  } catch (e) {
                    console.error("Error parsing optimistic message:", e);
                  }
                }

                if (optimisticData) {
                  // This is likely the thread created for this workspace message
                  optimisticMessage = {
                    ...optimisticData,
                    threadId: threadId,
                    thread_id: threadId,
                    message_index: 0,
                    isStreamComplete: true,
                    messageStatus: "completed",
                    metadata: {
                      ...optimisticData.metadata,
                      isOptimistic: true,
                      needsReplacement: true, // Flag to indicate this needs to be replaced by real message
                    },
                  };

                  // Clear the optimistic message from both store and localStorage
                  localStorage.removeItem("optimisticWorkspaceMessage");
                  set({ pendingWorkspaceMessage: null });
                }
              }
            }
          } catch (optimisticError) {
            console.error("Error loading optimistic message:", optimisticError);
            // Continue without the optimistic message
          }

          // *** UPDATED LOGIC FOR PRESERVING MESSAGES ***
          // Only preserve messages that legitimately belong to THIS thread
          // and aren't already in the database
          const currentMessages = messages || [];

          // We'll only preserve messages that:
          // 1. Have a matching threadId for the current thread
          // 2. Are not already in the formatted messages from DB
          // 3. Have a valid ID to ensure they're proper messages
          const messagesToPreserve = currentMessages.filter(
            (msg) =>
              (msg.threadId === threadId || msg.thread_id === threadId) &&
              msg.id &&
              !formattedMessages.some((fetchedMsg) => fetchedMsg.id === msg.id)
          );

          // Log breakdown of preserved messages by role
          const userMessagesToPreserve = messagesToPreserve.filter(
            (m) => m.role === "user"
          );
          const assistantMessagesToPreserve = messagesToPreserve.filter(
            (m) => m.role === "assistant"
          );

          // If we have preventNewMessageClear flag set, check for pendingUserMessage
          if (preventNewMessageClear === true) {
            const pendingUserMessage = currentState.pendingUserMessage;
            if (
              pendingUserMessage &&
              (pendingUserMessage.threadId === threadId ||
                pendingUserMessage.thread_id === threadId) &&
              !messagesToPreserve.some(
                (msg) => msg.id === pendingUserMessage.id
              ) &&
              !formattedMessages.some((msg) => msg.id === pendingUserMessage.id)
            ) {
              messagesToPreserve.push(pendingUserMessage);
            }
          }

          // Add messages to be preserved to the formatted messages
          if (messagesToPreserve.length > 0) {
            // Ensure all preserved messages have the correct threadId
            const messagesWithThreadId = messagesToPreserve.map((msg) => ({
              ...msg,
              threadId: threadId,
              thread_id: threadId,
            }));

            // Add preserved messages and re-sort to ensure chronological order
            formattedMessages = [...formattedMessages, ...messagesWithThreadId];

            // Sort again to ensure proper ordering with the combined messages
            formattedMessages.sort((a, b) => {
              const dateA = new Date(a.created_at);
              const dateB = new Date(b.created_at);
              return dateA - dateB;
            });
          }

          // Add any pending message from localStorage if it exists and wasn't already added
          if (
            pendingMessage &&
            !formattedMessages.some((msg) => msg.id === pendingMessage.id)
          ) {
            formattedMessages = [...formattedMessages, pendingMessage];

            // Ensure proper sorting again
            formattedMessages.sort((a, b) => {
              const dateA = new Date(a.created_at);
              const dateB = new Date(b.created_at);
              return dateA - dateB;
            });
          }

          // Add optimistic message if we have one
          if (
            optimisticMessage &&
            !formattedMessages.some((msg) => msg.id === optimisticMessage.id)
          ) {
            formattedMessages = [optimisticMessage, ...formattedMessages];
          }

          // Update both the messages and the threadMessages cache
          const threadMessagesMap = new Map(get().threadMessages || new Map());
          threadMessagesMap.set(threadId, formattedMessages);

          set({
            messages: formattedMessages,
            threadMessages: threadMessagesMap,
            currentThread: thread,
            currentThreadId: threadId,
            currentThreadTitle: thread.title,
            isLoading: false,
            error: null,
            // Reset the preventNewMessageClear flag after messages are loaded
            preventNewMessageClear: false,
          });

          return formattedMessages;
        } catch (error) {
          console.error(
            `Error loading messages for thread ${threadId}:`,
            error
          );
          set({
            error: error.message,
            isLoading: false,
            // Don't change messages array on error to preserve current state
          });
          return []; // Return empty array on error
        } finally {
          // Ensure loading state is turned off
          set({ isLoading: false });
        }
      },

      generateTitleFromUserMessage: async (userMessage) => {
        try {
          set({ isLoading: true, error: null });

          // Validate input
          if (!userMessage) {
            console.warn(
              "Invalid message format for title generation:",
              userMessage
            );
            return "Untitled Thread";
          }

          // Get user ID for tracking from auth store
          const authState = useAuthStore.getState();
          const userId = authState.user?.id || authState.userId;

          // Get the thread ID - first check if it was explicitly provided, then fall back to store
          const threadId = userMessage.threadId || get().currentThread?.id;

          // Get workspace ID if available
          const workspaceId =
            get().currentThread?.workspace_id || authState.currentWorkspace?.id;

          // Call the dedicated backend /title endpoint
          // This uses gpt-4.1-nano which is cheaper and properly tracks tokens
          const response = await fetch(`${API_BASE_URL}/api/ai/chat/title`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              userMessage: userMessage,
              tracking: {
                userId,
                threadId,
                workspaceId,
                featureId: "title_generation",
              },
            }),
          });

          if (!response.ok) {
            throw new Error(`Title generation failed: ${response.status}`);
          }

          const data = await response.json();

          if (!data.success || !data.title) {
            console.warn("Invalid title response from backend:", data);
            return "Untitled Thread";
          }

          const title = data.title.trim();

          // Validate the generated title
          if (!title || typeof title !== "string" || title.length < 2) {
            console.warn("Invalid title generated:", title);
            return "Untitled Thread";
          }

          // Remove any quotes or extra punctuation
          const cleanTitle = title
            .replace(/["'`]/g, "")
            .replace(/\.+$/, "")
            .trim();

          return cleanTitle;
        } catch (error) {
          console.error("Failed to generate thread title:", error);
          // Extract first few words of user message as fallback
          let fallbackTitle = "";

          if (typeof userMessage === "string") {
            fallbackTitle = userMessage.split(" ").slice(0, 6).join(" ");
          } else if (typeof userMessage.content === "string") {
            fallbackTitle = userMessage.content
              .split(" ")
              .slice(0, 6)
              .join(" ");
          }

          return fallbackTitle || "Untitled Thread";
        } finally {
          set({ isLoading: false });
        }
      },

      abortStream: () => {
        try {
          const currentState = get();
          if (
            currentState.abortController &&
            typeof currentState.abortController.abort === "function"
          ) {
            currentState.abortController.abort();
            set({
              isStreaming: false,
              streamStatus: "aborted",
              messageStatus: "aborted",
              isStreamComplete: true,
              isToolCallStreaming: false,
              toolExecutionActive: false,
              abortController: null,
            });
            return true;
          } else {
            console.warn("No valid abort controller found in thread store");
            set({
              isStreaming: false,
              streamStatus: "aborted",
              messageStatus: "aborted",
              isStreamComplete: true,
            });
            return false;
          }
        } catch (error) {
          console.error("Error in abortStream:", error);
          set({
            isStreaming: false,
            streamStatus: "aborted",
            messageStatus: "aborted",
            isStreamComplete: true,
            error: error.message,
          });
          return false;
        }
      },

      fetchThread: async (
        threadId,
        { useAdminClient = false, workspaceId = null } = {}
      ) => {
        try {
          if (!threadId) return null;
          const cachedThread = get().threads.find((t) => t.id === threadId);
          if (cachedThread) {
            return cachedThread;
          }
          if (workspaceId) {
            try {
              const { user } = useAuthStore.getState();
              if (!user?.id) throw new Error("User not authenticated");
              const sharedThread = await fetchSharedThreadData(
                threadId,
                user.id,
                workspaceId
              );
              if (sharedThread) {
                set((state) => ({
                  threads: [
                    ...state.threads.filter((t) => t.id !== threadId),
                    sharedThread,
                  ],
                }));
                return sharedThread;
              }
            } catch (sharedError) {
              console.error("Error fetching shared thread:", sharedError);
            }
          }
          const dbClient = useAdminClient ? supabaseAdmin : supabase;
          const { data, error } = await dbClient
            .from("threads")
            .select("*")
            .eq("id", threadId)
            .maybeSingle();
          if (error) {
            // Check if it's a PGRST116 error (thread doesn't exist)
            if (error.code === "PGRST116") {
              console.log(`Thread ${threadId} not found (PGRST116)`);
              return null;
            }
            console.error("Error fetching thread:", error);
            throw error;
          }
          // If data is null, thread doesn't exist
          if (!data) {
            return null;
          }
          set((state) => ({
            threads: [...state.threads.filter((t) => t.id !== threadId), data],
          }));
          return data;
        } catch (error) {
          // Check if it's a PGRST116 error (thread doesn't exist)
          if (error.code === "PGRST116") {
            console.log(`Thread ${threadId} not found (PGRST116)`);
            return null;
          }
          console.error("Error in fetchThread:", error);
          return null;
        }
      },

      fetchThreadMessages: async (threadId, options = {}) => {
        try {
          if (!threadId) {
            console.error("No thread ID provided");
            return [];
          }
          const { useAdminClient = false, workspaceId: _workspaceId = null } =
            options;
          const dbClient = useAdminClient ? supabaseAdmin : supabase;
          const { data: messages, error } = await dbClient
            .from("thread_messages")
            .select("*")
            .eq("thread_id", threadId)
            .order("created_at", { ascending: true });
          if (error) {
            console.error("Error fetching thread messages:", error);
            throw error;
          }
          if (!messages || messages.length === 0) {
            console.log(`No messages found for thread: ${threadId}`);
            return [];
          }
          const formattedMessages = messages.map((message) => ({
            id: message.message_id,
            threadId: message.thread_id,
            role: message.role,
            content: message.content,
            created_at: message.created_at,
            updated_at: message.updated_at || message.created_at,
            metadata: message.metadata || {},
            attachments: message.attachments || [],
            annotations: message.annotations || [],
            file_ids: message.file_ids || [],
            tool_calls: message.tool_calls || [],
            provider: message.provider || "openai",
            provider_message_id: message.provider_message_id,
            message_index: messages.indexOf(message),
            isStreamComplete: true,
            messageStatus: "completed",
          }));
          set({ messages: formattedMessages });
          return formattedMessages;
        } catch (error) {
          console.error("Error fetching thread messages:", error);
          return [];
        }
      },

      saveMessageToDatabase: async (messageData) => {
        try {
          if (!messageData || !messageData.id) {
            throw new Error("Invalid message data - missing ID");
          }

          const currentState = get();
          const currentThreadId =
            messageData.thread_id || currentState.currentThreadId;

          if (!currentThreadId) {
            throw new Error("No thread ID available for message");
          }

          // Prepare message data
          const preparedMessage = {
            ...messageData,
            thread_id: currentThreadId,
            workspace_id:
              messageData.workspace_id ||
              currentState.currentWorkspaceId ||
              useAuthStore.getState().currentWorkspaceId,
            created_at: messageData.created_at || new Date().toISOString(),
            updated_at: messageData.updated_at || new Date().toISOString(),
          };

          // Use the appropriate save function based on message type
          let saveResult = false;

          if (preparedMessage.role === "user") {
            saveResult = await saveUserMessage(preparedMessage);
          } else if (preparedMessage.role === "assistant") {
            if (
              preparedMessage.tool_calls &&
              preparedMessage.tool_calls.length > 0
            ) {
              saveResult = await saveToolCallMessage(preparedMessage);
            } else if (
              preparedMessage.metadata?.isFollowUpContent &&
              preparedMessage.metadata?.toolCallMessageId
            ) {
              saveResult = await saveFollowUpMessage(
                preparedMessage,
                preparedMessage.metadata.toolCallMessageId
              );
            } else {
              saveResult = await saveAssistantMessage(preparedMessage);
            }
          }

          // Return a result object for compatibility
          return {
            success: saveResult,
            id: preparedMessage.id,
            error: saveResult ? null : "Failed to save message",
          };
        } catch (error) {
          console.error(`Failed to save message:`, error);

          // Return a mock success to avoid breaking the UI flow
          return {
            id: messageData?.id,
            success: false,
            error: error.message,
          };
        }
      },

      updateMessage: async (messageId, updates) => {
        try {
          if (!messageId) {
            throw new Error("Message ID is required for updating");
          }

          const currentProvider = getCurrentProvider();

          const {
            isStreaming,
            isStreamComplete,
            messageStatus,
            tool_calls,
            thread_id: specificThreadId, // RACE CONDITION FIX: Accept specific thread ID
            ...dbSafeUpdates
          } = updates;

          const updatePayload = {
            ...dbSafeUpdates,
            updated_at: new Date().toISOString(),
            provider: currentProvider,
          };

          if (updates.provider_message_id) {
            updatePayload.provider_message_id = updates.provider_message_id;
          }

          // Handle tool calls
          if (updates.tool_calls) {
            updatePayload.tool_calls = updates.tool_calls;
            updatePayload.tools_count = Array.isArray(updates.tool_calls)
              ? updates.tool_calls.length
              : typeof updates.tool_calls === "object"
                ? 1
                : 0;
          }

          // RACE CONDITION FIX: Use specific thread ID if provided, otherwise fall back to current
          const threadIdToUse = specificThreadId || get().currentThreadId;

          if (!threadIdToUse) {
            throw new Error(
              "No thread ID available for message update - this prevents saving messages to the wrong thread or with null thread_id"
            );
          }

          console.log(
            `🔒 [THREAD STORE] updateMessage using thread ID: ${threadIdToUse} (specific: ${specificThreadId}, current: ${get().currentThreadId})`
          );

          // CRITICAL FIX: Get the existing message role to preserve it
          const existingMessage = get().messages?.find(
            (msg) => msg.id === messageId
          );
          const existingRole = existingMessage?.role;

          const upsertData = {
            id: messageId,
            message_id: messageId,
            ...updatePayload,
            role: updatePayload.role || existingRole || "assistant", // FIXED: Preserve existing role
            content: updatePayload.content || " ",
            thread_id: threadIdToUse, // RACE CONDITION FIX: Use the determined thread ID
            workspace_id:
              get().currentWorkspaceId ||
              useAuthStore.getState().currentWorkspaceId,
            created_at: updatePayload.created_at || new Date().toISOString(),
          };

          console.log("🔍 [THREAD STORE] updateMessage upsert data:", {
            messageId: upsertData.id,
            role: upsertData.role,
            providedRole: updatePayload.role,
            existingRole: existingRole,
            content: upsertData.content?.substring(0, 50),
          });

          const { data: updatedMessage, error } = await supabase
            .from("thread_messages")
            .upsert([upsertData], {
              onConflict: "id",
              ignoreDuplicates: false,
            })
            .select()
            .single();

          if (error) {
            console.error("Error updating message:", error);
            throw new Error(`Failed to update message: ${error.message}`);
          }

          set((state) => ({
            messages: state.messages.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    content:
                      updates.content !== undefined
                        ? updates.content
                        : msg.content,
                    // CRITICAL FIX: Merge metadata instead of replacing to preserve provider/model info
                    metadata:
                      updates.metadata !== undefined
                        ? {
                            ...(msg.metadata || {}),
                            ...updates.metadata,
                            // Ensure provider and model are always preserved
                            provider:
                              updates.metadata?.provider ||
                              msg.metadata?.provider,
                            model:
                              updates.metadata?.model || msg.metadata?.model,
                          }
                        : msg.metadata,
                    tool_calls:
                      updates.tool_calls !== undefined
                        ? updates.tool_calls
                        : msg.tool_calls,
                    // CRITICAL FIX: Use provider from metadata if available, otherwise use currentProvider
                    provider:
                      updates.metadata?.provider ||
                      msg.metadata?.provider ||
                      currentProvider,
                    provider_message_id:
                      updates.provider_message_id || msg.provider_message_id,
                    updated_at: updatedMessage.updated_at,
                    isStreaming:
                      updates.isStreaming !== undefined
                        ? updates.isStreaming
                        : msg.isStreaming,
                    isStreamComplete:
                      updates.isStreamComplete !== undefined
                        ? updates.isStreamComplete
                        : msg.isStreamComplete,
                    messageStatus:
                      updates.messageStatus !== undefined
                        ? updates.messageStatus
                        : msg.messageStatus,
                  }
                : msg
            ),
          }));

          return updatedMessage;
        } catch (error) {
          console.error("🔍 [THREAD STORE] updateMessage failed:", {
            messageId,
            error: error.message,
            errorCode: error.code,
            location: "threadStore.updateMessage - error",
            timestamp: new Date().toISOString(),
          });
          console.error("Error in updateMessage:", error);
          set({ error: error.message });
          return null;
        }
      },
      updateThreadTitle: async (threadId, title) => {
        if (!threadId || !title) return false;

        try {
          // First, get the current thread to access its existing metadata
          const { data: existingThread, error: fetchError } = await supabase
            .from("threads")
            .select("*")
            .eq("id", threadId)
            .single();

          if (fetchError) {
            return false;
          }

          // Create updated metadata by merging with existing metadata
          const existingMetadata = existingThread?.metadata || {};
          const updatedMetadata = {
            ...existingMetadata,
            thread_title: title,
            title_generated_at: new Date().toISOString(),
            title_source: "ai",
          };

          // Update thread title in Supabase with direct metadata update
          const { error } = await supabase
            .from("threads")
            .update({
              title: title,
              updated_at: new Date().toISOString(),
              metadata: updatedMetadata,
            })
            .eq("id", threadId);

          if (error) {
            console.error("Error updating thread title:", error);
            return false;
          }

          // Verify the update was successful with a separate query
          const { data: verifiedUpdate, error: verifyError } = await supabase
            .from("threads")
            .select("title, metadata")
            .eq("id", threadId)
            .single();

          if (verifyError) {
            console.error("Error verifying thread title update:", verifyError);
          }

          // Also update the threads array in the store
          set((state) => ({
            currentThreadTitle: title,
            currentThread:
              state.currentThread?.id === threadId
                ? {
                    ...state.currentThread,
                    title: title,
                    metadata: {
                      ...(state.currentThread?.metadata || {}),
                      thread_title: title,
                      title_generated_at: new Date().toISOString(),
                      title_source: "ai",
                    },
                  }
                : state.currentThread,
            threads: state.threads.map((thread) =>
              thread.id === threadId
                ? {
                    ...thread,
                    title: title,
                    metadata: {
                      ...(thread.metadata || {}),
                      thread_title: title,
                      title_generated_at: new Date().toISOString(),
                      title_source: "ai",
                    },
                  }
                : thread
            ),
          }));

          return true;
        } catch (error) {
          console.error("Error in updateThreadTitle:", error);
          return false;
        }
      },

      resetStore: () => {
        // Clean up subscription channel before resetting
        const currentState = get();
        if (currentState.threadSubscriptionChannel) {
          console.log(
            "[ThreadStore] Cleaning up thread subscription channel on reset"
          );
          const { supabase: supabaseClient } = currentState.clients || {};
          if (supabaseClient) {
            supabaseClient.removeChannel(
              currentState.threadSubscriptionChannel
            );
          } else {
            // Fallback to imported supabase if clients not available
            supabase.removeChannel(currentState.threadSubscriptionChannel);
          }
        }

        set({
          ...INITIAL_STORE_STATE,
          ...INITIAL_MESSAGE_STATE,
          ...INITIAL_THREAD_STATE,
          ...INITIAL_RUN_STATE,
          ...INITIAL_TOOL_STATE,
          ...INITIAL_STREAM_STATE,
          ...INITIAL_COMPUTED_STATE,
          userId: null,
          currentWorkspaceId: null,
          currentAssistantId: null,
        });
      },

      resetToolCallState: () => {
        set({
          currentToolCallName: null,
          currentToolCallId: null,
          currentToolCallOutput: null,
          toolExecutionActive: false,
          isToolCallStreaming: false,
        });
      },

      /**
       * Detect if there's a stale streaming state (isStreaming true but no AbortController)
       * This happens when the page reloads during streaming
       */
      hasStaleStreamingState: () => {
        const state = get();
        // Check if global isStreaming is true but no abortController
        if (state.isStreaming && !state.abortController) {
          return true;
        }
        // Check if any message has isStreaming true but no abortController
        const messages = state.messages || [];
        const currentMessageId = state.currentMessageId;
        const hasStreamingMessage = messages.some(
          (msg) => msg.isStreaming === true && msg.id === currentMessageId
        );
        return hasStreamingMessage && !state.abortController;
      },

      /**
       * Clear stale streaming state - resets isStreaming flags on messages and store
       * Used when canceling a "stuck" streaming state after page reload
       */
      clearStaleStreamingState: () => {
        const state = get();
        console.log("[ThreadStore] Clearing stale streaming state");

        // Update messages to clear isStreaming flag
        const updatedMessages = (state.messages || []).map((msg) => {
          if (msg.isStreaming === true) {
            return {
              ...msg,
              isStreaming: false,
              isStreamComplete: true,
              messageStatus: "cancelled",
            };
          }
          return msg;
        });

        // Also update the threadMessages Map if the current thread is set
        const threadId = state.currentThreadId;
        let updatedThreadMessagesMap = state.threadMessages;
        if (threadId && state.threadMessages) {
          updatedThreadMessagesMap = new Map(state.threadMessages);
          updatedThreadMessagesMap.set(threadId, updatedMessages);
        }

        set({
          messages: updatedMessages,
          threadMessages: updatedThreadMessagesMap,
          isStreaming: false,
          streamStatus: "cancelled",
          messageStatus: "cancelled",
          isStreamComplete: true,
          abortController: null,
          currentToolCallName: null,
          currentToolCallId: null,
          currentToolCallOutput: null,
        });
      },

      // Enhanced title generation with thread store state management
      enqueueTitleGeneration: (threadId, content) => {
        if (!threadId || !content) return;

        const state = get();
        const newQueue = new Map(state.titleGenerationQueue);

        // Check if already queued or in progress
        if (
          newQueue.has(threadId) ||
          state.titleGenerationInProgress.has(threadId)
        ) {
          return;
        }

        newQueue.set(threadId, {
          content: content.trim(),
          status: "pending",
          attempts: 0,
          timestamp: Date.now(),
          threadId,
        });

        set({ titleGenerationQueue: newQueue });

        // Process the queue immediately
        setTimeout(() => get().processTitleGenerationQueue(), 100);
      },

      processTitleGenerationQueue: async () => {
        const state = get();
        const queue = state.titleGenerationQueue;

        if (queue.size === 0) return;

        // Process one item at a time to avoid rate limiting
        for (const [threadId, queueItem] of queue.entries()) {
          if (queueItem.status !== "pending") continue;
          if (state.titleGenerationInProgress.has(threadId)) continue;

          // Check if thread needs title generation
          const needsTitle = await get().checkThreadNeedsTitle(threadId);
          if (!needsTitle) {
            // Remove from queue if no longer needs title
            get().removeTitleFromQueue(threadId);
            continue;
          }

          // Start generation
          await get().generateAndUpdateTitle(threadId, queueItem.content);
          break; // Process one at a time
        }

        // Schedule next processing if queue not empty
        if (state.titleGenerationQueue.size > 0) {
          setTimeout(() => get().processTitleGenerationQueue(), 2000);
        }
      },

      checkThreadNeedsTitle: async (threadId) => {
        try {
          // Get thread from database to check current title
          const { data: thread, error } = await supabase
            .from("threads")
            .select("title, metadata")
            .eq("id", threadId)
            .single();

          if (error || !thread) return false;

          // Check if title is default/needs generation
          const hasDefaultTitle =
            !thread.title ||
            thread.title === "New Chat" ||
            thread.title === "New Thread" ||
            thread.title === "Untitled Thread" ||
            thread.title.startsWith("New chat in ") ||
            thread.title.includes("Workspace");

          // Check metadata flags
          const needsGeneration =
            thread.metadata?.needs_title_generation === true;

          return hasDefaultTitle || needsGeneration;
        } catch (error) {
          console.error("Error checking if thread needs title:", error);
          return false;
        }
      },

      generateAndUpdateTitle: async (threadId, content) => {
        const state = get();

        // Mark as in progress
        const newInProgress = new Set(state.titleGenerationInProgress);
        newInProgress.add(threadId);
        set({ titleGenerationInProgress: newInProgress });

        // Update queue item status
        const newQueue = new Map(state.titleGenerationQueue);
        const queueItem = newQueue.get(threadId);
        if (queueItem) {
          queueItem.status = "generating";
          queueItem.attempts += 1;
          newQueue.set(threadId, queueItem);
          set({ titleGenerationQueue: newQueue });
        }

        try {
          // Check attempts limit
          if (queueItem && queueItem.attempts > 2) {
            console.warn(
              `Title generation failed after ${queueItem.attempts} attempts for thread ${threadId}`
            );
            get().removeTitleFromQueue(threadId);
            return;
          }

          // Generate title using existing method
          const title = await get().generateTitleFromUserMessage({
            content,
            threadId,
          });

          if (title && title !== "Untitled Thread") {
            // Update thread title in database
            const updateSuccess = await get().updateThreadTitle(
              threadId,
              title
            );

            if (updateSuccess) {
              // Update metadata to clear generation flags
              await supabase
                .from("threads")
                .update({
                  metadata: {
                    needs_title_generation: false,
                    title_generated_at: new Date().toISOString(),
                    title_source: "ai",
                  },
                })
                .eq("id", threadId);

              // Remove from queue on success
              get().removeTitleFromQueue(threadId);

              console.log(
                `✅ Title generated successfully for thread ${threadId}: "${title}"`
              );
            } else {
              // Mark as failed, will retry later
              if (queueItem) {
                queueItem.status = "failed";
                newQueue.set(threadId, queueItem);
                set({ titleGenerationQueue: newQueue });
              }
            }
          } else {
            // Invalid title generated
            if (queueItem) {
              queueItem.status = "failed";
              newQueue.set(threadId, queueItem);
              set({ titleGenerationQueue: newQueue });
            }
          }
        } catch (error) {
          console.error(
            `Error generating title for thread ${threadId}:`,
            error
          );

          // Mark as failed
          if (queueItem) {
            queueItem.status = "failed";
            newQueue.set(threadId, queueItem);
            set({ titleGenerationQueue: newQueue });
          }
        } finally {
          // Remove from in progress
          const updatedInProgress = new Set(state.titleGenerationInProgress);
          updatedInProgress.delete(threadId);
          set({ titleGenerationInProgress: updatedInProgress });
        }
      },

      removeTitleFromQueue: (threadId) => {
        const state = get();
        const newQueue = new Map(state.titleGenerationQueue);
        const newInProgress = new Set(state.titleGenerationInProgress);

        newQueue.delete(threadId);
        newInProgress.delete(threadId);

        set({
          titleGenerationQueue: newQueue,
          titleGenerationInProgress: newInProgress,
        });
      },

      // Immediate title generation for first messages
      generateTitleImmediately: async (threadId, content) => {
        if (!threadId || !content) return;

        try {
          // Check if thread needs title
          const needsTitle = await get().checkThreadNeedsTitle(threadId);
          if (!needsTitle) return;

          // Generate and update immediately
          await get().generateAndUpdateTitle(threadId, content);
        } catch (error) {
          console.error("Error in immediate title generation:", error);
          // Fallback to queue
          get().enqueueTitleGeneration(threadId, content);
        }
      },
    }),
    {
      name: "thread-storage",
      partialize: (state) => {
        try {
          // Helper function to recursively remove non-serializable properties
          const removeNonSerializable = (obj) => {
            if (!obj || typeof obj !== "object") return obj;

            // Handle arrays
            if (Array.isArray(obj)) {
              return obj.map((item) => removeNonSerializable(item));
            }

            // Create a new object without non-serializable properties
            const clean = {};
            for (const [key, value] of Object.entries(obj)) {
              // Skip functions and known non-serializable objects/properties
              if (
                typeof value === "function" ||
                key === "_client" ||
                key === "client" ||
                key === "openai" ||
                key === "supabase" ||
                key === "abortController" ||
                key === "clients" ||
                key === "emit" ||
                key === "on" ||
                key === "off" ||
                key === "onEvent" ||
                value instanceof AbortController
              ) {
                continue;
              }

              // Recursively clean objects
              if (value && typeof value === "object") {
                clean[key] = removeNonSerializable(value);
              } else {
                clean[key] = value;
              }
            }
            return clean;
          };

          const serializableState = {
            userId: state.userId,
            currentWorkspaceId: state.currentWorkspaceId,
            // thread state
            threads: removeNonSerializable(state.threads),
            currentThread: removeNonSerializable(state.currentThread),
            currentThreadId: state.currentThreadId,
            currentThreadTitle: state.currentThreadTitle,
            currentThreadContext: removeNonSerializable(
              state.currentThreadContext
            ),
            // provider state
            currentProvider: getCurrentProvider(), // Store current provider

            // persistence flags for user messages
            preventNewMessageClear: state.preventNewMessageClear,
            pendingUserMessage: removeNonSerializable(state.pendingUserMessage),
            pendingUserMessageId: state.pendingUserMessageId,
            pendingWorkspaceMessage: removeNonSerializable(
              state.pendingWorkspaceMessage
            ),

            // stream state
            streamStatus: state.streamStatus,
            isStreaming: state.isStreaming,
            streamingToolContent: state.streamingToolContent,
            streamingToolContentComplete: state.streamingToolContentComplete,

            // tool state
            currentToolCallId: state.currentToolCallId,
            currentToolCallName: state.currentToolCallName,
            currentToolCallOutput: state.currentToolCallOutput,
            currentMessageId: state.currentMessageId,
            messages: removeNonSerializable(state.messages),
            // NOTE: Do NOT persist 'initialized' - threads must be fetched fresh on each page load
            // to ensure the user sees all their threads, not stale cached data
            isLoading: state.isLoading,

            // Convert Maps to clean serializable objects
            messageAnnotations: Object.fromEntries(
              Array.from(state.messageAnnotations || new Map()).map(
                ([k, v]) => [k, removeNonSerializable(v)]
              )
            ),
            toolInputs: Object.fromEntries(
              Array.from(state.toolInputs || new Map()).map(([k, v]) => [
                k,
                removeNonSerializable(v),
              ])
            ),
            toolOutputs: Object.fromEntries(
              Array.from(state.toolOutputs || new Map()).map(([k, v]) => [
                k,
                removeNonSerializable(v),
              ])
            ),
            pendingToolOutputs: Object.fromEntries(
              Array.from(state.pendingToolOutputs || new Map()).map(
                ([k, v]) => [k, removeNonSerializable(v)]
              )
            ),
            activeRuns: Object.fromEntries(
              Array.from(state.activeRuns || new Map()).map(([k, v]) => [
                k,
                removeNonSerializable(v),
              ])
            ),
          };

          return serializableState;
        } catch (e) {
          console.error("Error in thread store serialization:", e);
          // Fallback to minimal safe state if serialization fails
          return {
            userId: state.userId,
            currentWorkspaceId: state.currentWorkspaceId,
            currentThreadId: state.currentThreadId,
            currentThreadTitle: state.currentThreadTitle,
            initialized: state.initialized,
          };
        }
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          // console.log("Rehydrating thread store:", state);
          const { userId, currentWorkspaceId } = state;
          const currentThread = state.currentThread;
          const currentThreadId = currentThread?.id;
          const threadMessages = state.messages;
          const threadMessagesMap = state.threadMessages;
          const searchResults = state.searchResults;
          const streamingToolContent = state.streamingToolContent;
          const streamingToolContentComplete =
            state.streamingToolContentComplete;
          const initialized = state.initialized;
          const currentThreadContext = state.currentThreadContext;
          const runSettings = state.runSettings;
          const currentThreadUsageHistory = state.currentThreadUsageHistory;
          const currentUsage = state.currentUsage;
          const toolState = state.toolState;
          const toolInputs = state.toolInputs;
          const toolOutputs = state.toolOutputs;
          const pendingToolOutputs = state.pendingToolOutputs;
          const activeRuns = state.activeRuns;
          const storedProvider = state.currentProvider || "openai";
          // Restore message persistence flags
          const preventNewMessageClear = state.preventNewMessageClear || false;
          const pendingUserMessage = state.pendingUserMessage || null;
          const pendingUserMessageId = state.pendingUserMessageId || null;
          const pendingWorkspaceMessage = state.pendingWorkspaceMessage || null;

          state.userId = userId;
          state.currentWorkspaceId = currentWorkspaceId;
          state.currentThread = currentThread;
          state.currentThreadId = currentThreadId;
          state.messages = threadMessages;
          state.threadMessages = threadMessagesMap;
          state.searchResults = searchResults;
          state.streamingToolContent = streamingToolContent;
          state.streamingToolContentComplete = streamingToolContentComplete;
          state.initialized = initialized;
          state.currentThreadUsageHistory = currentThreadUsageHistory;
          state.currentUsage = currentUsage;
          state.toolState = toolState;
          state.runSettings = runSettings;
          state.currentThreadContext = currentThreadContext;
          // Restore the message persistence flags
          state.preventNewMessageClear = preventNewMessageClear;
          state.pendingUserMessage = pendingUserMessage;
          state.pendingUserMessageId = pendingUserMessageId;
          state.pendingWorkspaceMessage = pendingWorkspaceMessage;
          state.messageAnnotations = new Map(
            Object.entries(state.messageAnnotations || {})
          );
          state.toolInputs = new Map(Object.entries(toolInputs || {}));
          state.toolOutputs = new Map(Object.entries(toolOutputs || {}));
          state.pendingToolOutputs = new Map(
            Object.entries(pendingToolOutputs || {})
          );
          state.activeRuns = new Map(Object.entries(activeRuns || {}));
          state.clients = {
            supabase: supabase,
          };

          // Restore the correct provider based on thread/assistant
          if (currentThread?.metadata?.provider) {
            // If thread metadata has provider info, use that
            switchProvider(currentThread.metadata.provider);
          } else if (storedProvider !== "openai") {
            // If we stored a non-default provider, restore it
            switchProvider(storedProvider);
          } else if (currentThread?.model) {
            // If thread has model info, determine provider from that
            switchProvider(currentThread.model);
          }
        }
      },
    }
  )
);
// Export utilities for access from components that use thread store
export { SupabaseThreadUtils };

export default useThreadStore;
